import AddIcon from "@mui/icons-material/Add";
import { ArrowDropDown, Delete, Edit, FileDownload, FilterAlt, FilterAltOff, Payment } from "@mui/icons-material";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    ListItemIcon,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmacaoAcaoModal } from "../../../core/components/ConfirmacaoAcaoModal";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { ContaManualFormModal } from "../../components/ContaManualFormModal";
import ContaManualTableRow from "../../components/ContaManualTableRow";
import { useContas } from "../../hooks/useContas";
import { useFetchContas } from "../../hooks/useFetchContas";
import { IConta } from "../../interfaces/IConta";
import { IFiltro } from "../../../core/interfaces/IFiltro";
import { ReferenciaConta } from "../../enum/ReferenciaConta";

const labelStyle = {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'text.secondary',
    mb: 0.5,
    display: 'block'
};

const colHead = {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'text.secondary',
    py: 1.5,
    bgcolor: 'grey.50'
};

export const LancamentoContas = () => {
    const navigate = useNavigate();
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();
    const { dados, isLoading, mutate, handlePaginaChange, filtrar, limpar } = useFetchContas();
    const { criarContaManual, editarContaManual, excluirContaManual, exportarContas, loading } = useContas();

    const [modalAberto, setModalAberto] = useState(false);
    const [contaSelecionada, setContaSelecionada] = useState<IConta | undefined>(undefined);
    const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
    const [loadingAcao, setLoadingAcao] = useState(false);

    const [filtroTipo, setFiltroTipo] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroDescricao, setFiltroDescricao] = useState('');
    const [dataInicio, setDataInicio] = useState<Moment | null>(null);
    const [dataFim, setDataFim] = useState<Moment | null>(null);

    const [selecionadas, setSelecionadas] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    useEffect(() => {
        if (perfil && !verificarAcessoFuncionalidade("Gestão de caixa")) {
            navigate("/nao-autorizado");
        }
    }, [perfil]);

    useEffect(() => { setSelecionadas([]); }, [dados]);

    const toggleSelecionado = (uuid: string) => {
        setSelecionadas(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const abrirAcoes = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    const contasSelecionadas = useMemo(
        () => dados?.itens?.filter(c => selecionadas.includes(c.uuid)) ?? [],
        [selecionadas, dados]
    );

    const tiposUnicos = useMemo(
        () => new Set(contasSelecionadas.map(c => c.tipo ?? 'pedido')),
        [contasSelecionadas]
    );
    const pagosUnicos = useMemo(
        () => new Set(contasSelecionadas.map(c => !!c.pago)),
        [contasSelecionadas]
    );

    const todosMesmoTipo = tiposUnicos.size <= 1;
    const todosMesmoPago = pagosUnicos.size <= 1;
    const pagamentoHabilitado = selecionadas.length > 0 && todosMesmoTipo && todosMesmoPago;
    const todosEstaoPagos = contasSelecionadas.every(c => c.pago);
    const labelPagamento = todosEstaoPagos ? 'Remover pagamento' : 'Adicionar pagamento';

    let erroPagamento: string | null = null;
    if (selecionadas.length > 0) {
        if (!todosMesmoTipo) erroPagamento = 'Selecione contas do mesmo tipo';
        else if (!todosMesmoPago) erroPagamento = 'Selecione contas com o mesmo status de pagamento';
    }

    const handleFiltrar = () => {
        const filtros: IFiltro[] = [];
        if (filtroTipo) filtros.push({ chave: 'tipo', valor: filtroTipo });
        if (filtroStatus !== '') filtros.push({ chave: 'pago', valor: filtroStatus });
        if (filtroDescricao) filtros.push({ chave: 'descricao', valor: filtroDescricao });
        if (dataInicio) filtros.push({ chave: 'dataInicio', valor: dataInicio.format('YYYY-MM-DD') });
        if (dataFim) filtros.push({ chave: 'dataFim', valor: dataFim.format('YYYY-MM-DD') });
        filtrar(filtros);
    };

    const handleLimpar = () => {
        setFiltroTipo('');
        setFiltroStatus('');
        setFiltroDescricao('');
        setDataInicio(null);
        setDataFim(null);
        limpar();
    };

    const handleExportar = () => {
        exportarContas({
            tipo: filtroTipo || undefined,
            pago: filtroStatus !== '' ? filtroStatus : undefined,
            descricao: filtroDescricao || undefined,
            dataInicio: dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
            dataFim: dataFim ? dataFim.format('YYYY-MM-DD') : undefined,
        });
    };

    const handleNovaConta = () => {
        setContaSelecionada(undefined);
        setModalAberto(true);
    };

    const handleEditar = () => {
        if (contasSelecionadas.length !== 1) return;
        setContaSelecionada(contasSelecionadas[0]);
        setModalAberto(true);
        fecharAcoes();
    };

    const handleExcluir = () => {
        fecharAcoes();
        setConfirmacaoAberta(true);
    };

    const handleConfirmarExclusao = async () => {
        setLoadingAcao(true);
        for (const uuid of selecionadas) {
            await excluirContaManual(uuid);
        }
        setLoadingAcao(false);
        setConfirmacaoAberta(false);
        setSelecionadas([]);
        mutate();
    };

    const handleAtualizarPagamento = async () => {
        fecharAcoes();
        const novoPago = !todosEstaoPagos;
        setLoadingAcao(true);
        for (const uuid of selecionadas) {
            const conta = contasSelecionadas.find(c => c.uuid === uuid);
            if (conta?.referenciaTipo === ReferenciaConta.manual) {
                await editarContaManual(uuid, { pago: novoPago });
            }
        }
        setLoadingAcao(false);
        setSelecionadas([]);
        mutate();
    };

    const handleSalvar = async (dadosForm: any | any[]) => {
        if (Array.isArray(dadosForm)) {
            for (const dados of dadosForm) {
                await criarContaManual(dados);
            }
        } else if (contaSelecionada) {
            await editarContaManual(contaSelecionada.uuid, dadosForm);
        } else {
            await criarContaManual(dadosForm);
        }
        setModalAberto(false);
        mutate();
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>

            {/* Título + Nova Conta */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="text.primary" letterSpacing="-0.5px">
                    Contas
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNovaConta}
                    sx={{ fontWeight: 700, px: 3, borderRadius: 2, boxShadow: 2 }}
                >
                    Nova Conta
                </Button>
            </Box>

            {/* Card de Filtros */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} lg>
                        <Typography sx={labelStyle}>Descrição</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar por descrição..."
                            value={filtroDescricao}
                            onChange={(e) => setFiltroDescricao(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleFiltrar()}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <Typography sx={labelStyle}>Tipo</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">Todos os tipos</MenuItem>
                                <MenuItem value="pagar">A Pagar</MenuItem>
                                <MenuItem value="receber">A Receber</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <Typography sx={labelStyle}>Status</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filtroStatus}
                                onChange={(e) => setFiltroStatus(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">Todos os status</MenuItem>
                                <MenuItem value="false">Pendente</MenuItem>
                                <MenuItem value="true">Pago</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <Typography sx={labelStyle}>Data Início</Typography>
                        <DatePicker
                            value={dataInicio}
                            onChange={setDataInicio}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <Typography sx={labelStyle}>Data Fim</Typography>
                        <DatePicker
                            value={dataFim}
                            onChange={setDataFim}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<FilterAltOff />}
                            onClick={handleLimpar}
                            sx={{ fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<FilterAlt />}
                            onClick={handleFiltrar}
                            sx={{ fontWeight: 700, boxShadow: 1 }}
                        >
                            Filtrar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={loading ? <CircularProgress size={16} /> : <FileDownload />}
                            onClick={handleExportar}
                            disabled={loading || isLoading}
                            sx={{ fontWeight: 600 }}
                        >
                            Exportar
                        </Button>
                    </Box>

                    <Button
                        variant="outlined"
                        disabled={selecionadas.length === 0}
                        endIcon={
                            <ArrowDropDown sx={{
                                transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s'
                            }} />
                        }
                        onClick={abrirAcoes}
                        sx={{ fontWeight: 600 }}
                    >
                        Ações {selecionadas.length > 0 && `(${selecionadas.length})`}
                    </Button>

                    <Menu anchorEl={anchorEl} open={openAcoes} onClose={fecharAcoes} elevation={3}>
                        <MenuItem disabled>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={1}>
                                {selecionadas.length} conta{selecionadas.length !== 1 ? 's' : ''} selecionada{selecionadas.length !== 1 ? 's' : ''}
                            </Typography>
                        </MenuItem>
                        <Divider />
                        <Tooltip title={selecionadas.length !== 1 ? 'Selecione exatamente 1 conta para editar' : ''} placement="left">
                            <span>
                                <MenuItem onClick={handleEditar} disabled={selecionadas.length !== 1}>
                                    <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                                    <Typography variant="inherit">Editar</Typography>
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <MenuItem onClick={handleExcluir}>
                            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Excluir</Typography>
                        </MenuItem>
                        <Divider />
                        <Tooltip title={erroPagamento ?? ''} placement="left">
                            <span>
                                <MenuItem onClick={handleAtualizarPagamento} disabled={!pagamentoHabilitado}>
                                    <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
                                    <Typography variant="inherit">{labelPagamento}</Typography>
                                </MenuItem>
                            </span>
                        </Tooltip>
                    </Menu>
                </Box>
            </Paper>

            {/* Tabela */}
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {isLoading || loadingAcao ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ ...colHead, pl: 2 }} />
                                    <TableCell sx={colHead}>Descrição</TableCell>
                                    <TableCell sx={colHead}>Valor</TableCell>
                                    <TableCell sx={colHead}>Tipo</TableCell>
                                    <TableCell align="center" sx={colHead}>Vencimento</TableCell>
                                    <TableCell align="center" sx={colHead}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dados?.itens?.length ? (
                                    dados.itens.map((conta) => (
                                        <ContaManualTableRow
                                            key={conta.uuid}
                                            conta={conta}
                                            selecionado={selecionadas.includes(conta.uuid)}
                                            toggleSelecionado={toggleSelecionado}
                                        />
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                            Nenhuma conta encontrada
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            px: 3, py: 2, bgcolor: 'grey.50'
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                Exibindo{' '}
                                <Box component="span" fontWeight={700} color="text.primary">{dados?.itens?.length ?? 0}</Box>
                                {' '}de{' '}
                                <Box component="span" fontWeight={700} color="text.primary">{dados?.quantidadeTotalItens ?? 0}</Box>
                                {' '}registros
                            </Typography>
                            {dados && dados.quantidadeTotalPaginas > 1 && (
                                <Pagination
                                    count={dados.quantidadeTotalPaginas}
                                    page={dados.paginaAtual}
                                    onChange={handlePaginaChange}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={2}
                                    boundaryCount={2}
                                    size="small"
                                />
                            )}
                        </Box>
                    </TableContainer>
                )}
            </Paper>

            <ContaManualFormModal
                open={modalAberto}
                onFechar={() => setModalAberto(false)}
                onSalvar={handleSalvar}
                conta={contaSelecionada}
                loading={loading}
            />

            <ConfirmacaoAcaoModal
                open={confirmacaoAberta}
                titulo="Excluir contas"
                descricao={`Tem certeza que deseja excluir ${selecionadas.length} conta${selecionadas.length !== 1 ? 's' : ''}? A ação não pode ser revertida.`}
                onConfirmar={handleConfirmarExclusao}
                onCancelar={() => setConfirmacaoAberta(false)}
                loading={loadingAcao}
            />
        </Box>
    );
};
